import { IconButton, ActionDropdown, confirmAlert } from 'components/common';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { deactivateUser, activateUser, searchUser, unlockUser, getUser, syncStatusUser, deleteUser } from 'redux/authorization/userSlice';
import UserDetailChangePassword from './UserDetailChangePassword';
import UserFormModal from '../UserFormModal';
import { MdDelete, MdLock, MdSync, MdVisibility } from 'react-icons/md';
import PropTypes from 'prop-types';
import { FaUnlock, FaArrowLeft, FaSyncAlt, FaUserLock, FaEdit, FaList, FaUser, FaEllipsisV } from 'react-icons/fa';
import ImpersonateModal from './ImpersonateModal';
import { isImpersonating } from 'helpers/impersonation';

const UserDetailHeader = ({user}) => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showImpersonate,    setShowImpersonate]    = useState(false);
  const currentUser = useSelector((state) => state.profile_user?.data);
  const [showUserModal,      setShowUserModal]      = useState(false);
  const [editUserData,       setEditUserData]       = useState(null);
  const [valueSearch,        setValueSearch]        = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userUrl = "/authorization/users";
  const userId = user?.id;

  const canImpersonate =
    Boolean(currentUser?.is_admin) &&
    Boolean(user?.id) &&
    user.id !== currentUser?.id &&
    !user?.admin &&
    user?.active !== false &&
    !isImpersonating();

  const handleNewuser = () => {
    setEditUserData(null);
    setShowUserModal(true);
  };

  const handleEditUser = () => {
    setEditUserData(user);
    setShowUserModal(true);
  };

  const handleUserModalSuccess = () => {
    setShowUserModal(false);
    dispatch(getUser(userId));
  };

  const handleDeleteUser = () => {
    confirmAlert({
      title: "Confirmation!",
      message: `Are you sure you want to delete user <strong>${user.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-danger',
          onClick: async () => {
            try {
              await dispatch(deleteUser(user.id));
            } catch (error) {
              toast.error(error?.message || 'Failed to delete user');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {} // Do nothing on cancel
        }
      ]
    });
  }

  const handleUserList = () => {
    navigate(userUrl)
  }

  const handleActivate = async () => {
    confirmAlert({
      title: "Confirmation!",
      message: `Are you sure you want to activate user <strong>${user.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-primary',
          onClick: async () => {
            try {
              await dispatch(activateUser(user.id));
            } catch (error) {
              toast.error(error?.message || 'Failed to update user');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {} // Do nothing on cancel
        }
      ]
    });
  }

  const handleDeactivate = async () => {
    confirmAlert({
      title: "Confirmation!",
      message: `Are you sure you want to deactivate user <strong>${user.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-primary',
          onClick: async () => {
            try {
              await dispatch(deactivateUser(user.id));
            } catch (error) {
              toast.error(error?.message || 'Failed to update user');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {} // Do nothing on cancel
        }
      ]
    });
  }

  const handleSyncStatus = async () => {
    confirmAlert({
      title: "Confirmation!",
      message: `Are you sure you want to sync status CRM Opla, LDAP, Google Workspace for user <strong>${user.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-primary',
          onClick: async () => {
            try {
              await dispatch(syncStatusUser(user.id)).unwrap();
            } catch (error) {
              toast.error(error?.message || 'Failed to sync status');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {} // Do nothing on cancel
        }
      ]
    });
  }

  const handleChangePassword = () => {
    setShowChangePassword(true)
  }

  // const handleBypassConfirmation = () => {
  //   dispatch(confirmUser(userId))
  // }

  const handleUnlock = () => {
    dispatch(unlockUser(userId))
  }

  const handleChange = (e) => {
    const {value} = e.target;
    setValueSearch(value)
  }

  useEffect(() => {
    const handleSeach = async () => {
      const params = {q: valueSearch.toUpperCase()}
      const response = await dispatch(searchUser(params));
      const {user_id, message} = response.payload;

      if (user_id) {
        navigate(`/authorization/users/${user_id}`);
        toast.success(message)
      }
      else {
        toast.error(message)
      }
    }

    if (valueSearch && valueSearch.length > 2) {
      handleSeach()
    }
  }, [dispatch, navigate, valueSearch]);


  const handleRefreshUser = async () => {
    await dispatch(getUser(userId)).unwrap();
  }

  const activeUser = user?.active || false;

  return (
    <>
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <div className="tw-flex tw-items-center tw-gap-2" style={{ width: '40%' }}>
            <IconButton icon={<FaArrowLeft size={12} />} variant="secondary" size="sm" onClick={() => navigate(-1)} />
            <div className="tw-flex tw-items-center tw-rounded-md tw-overflow-hidden tw-border tw-border-gray-300 tw-ml-2" style={{ flexGrow: 1 }}>
              <input
                size="sm"
                placeholder="Search name..."
                onChange={handleChange}
                className="tw-flex-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border-0 focus:tw-outline-none"
              />
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <IconButton icon={<FaUser />} variant="secondary" size="sm" onClick={handleNewuser}>
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New user</span>
            </IconButton>
            <IconButton icon={<FaSyncAlt size={12} />} variant="secondary" size="sm" onClick={handleRefreshUser} title="Refresh" />

            {activeUser ? (
              <IconButton icon={<FaUserLock size={12} />} variant="primary" size="sm" onClick={handleDeactivate}>
                <span className="tw-hidden xl:tw-inline-block tw-ml-1">Deactivate</span>
              </IconButton>
            ) : (
              <IconButton icon={<FaUnlock size={12} />} variant="primary" size="sm" onClick={handleActivate}>
                <span className="tw-hidden xl:tw-inline-block tw-ml-1">Activate</span>
              </IconButton>
            )}

            {/* Replaces administrators borrowing this person's password to look at
                their data: nobody shares a password, the session names both
                people, and every audited action carries the administrator's name.

                Hidden in the three cases the API also refuses, so the button is
                never offered for an action that would come back rejected:
                non-admins, yourself, another administrator, and a deactivated
                account. Also hidden while already impersonating — the stored
                original identity holds one account, and stacking a second would
                strand the real one. */}
            {canImpersonate && (
              <IconButton
                icon={<MdVisibility size={13} />}
                variant="secondary"
                size="sm"
                className="tw-hidden sm:tw-inline-flex tw-mr-1"
                title={`See the portal as ${user?.name} without their password`}
                onClick={() => setShowImpersonate(true)}
              >
                View as
              </IconButton>
            )}

            <IconButton icon={<FaEdit size={12} />} variant="secondary" size="sm" className="tw-hidden sm:tw-inline-flex tw-mr-1" onClick={handleEditUser}>
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">Edit</span>
            </IconButton>

            <IconButton icon={<FaList size={12} />} variant="secondary" size="sm" className="tw-hidden sm:tw-inline-flex tw-mr-1" onClick={handleUserList}>
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">List</span>
            </IconButton>

            <ActionDropdown
              align="end"
              menuWidth={230}
              trigger={<FaEllipsisV className="tw-text-[11px]" />}
              items={[
                { label: 'Change password', icon: <MdLock size={14} />, onClick: handleChangePassword },
                { divider: true },
                { label: 'Sync status external accounts', icon: <MdSync size={14} />, onClick: handleSyncStatus },
                ...(user?.access_locked
                  ? [{ label: 'Unlock', icon: <FaUnlock size={13} />, onClick: handleUnlock }]
                  : []),
                { divider: true },
                { label: 'Delete', icon: <MdDelete size={14} />, danger: true, onClick: handleDeleteUser },
              ]}
            />
          </div>
        </div>
      </div>

      {showImpersonate && (
        <ImpersonateModal user={user} open={showImpersonate} setOpen={setShowImpersonate} />
      )}

      <UserDetailChangePassword
        user={user}
        showModal={showChangePassword}
        setshowModal={setShowChangePassword}
      />

      <UserFormModal
        show={showUserModal}
        user={editUserData}
        onHide={() => setShowUserModal(false)}
        onSuccess={handleUserModalSuccess}
      />
    </>
  );
};

UserDetailHeader.propTypes = {
  user: PropTypes.object.isRequired
}

export default UserDetailHeader;
